/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Julia Ignatieva
 * @version 1.29.0
 * @since 1.13.0
 */

import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { icons } from '@assets/index';
import { TableFooterNameType } from '@components/Table/types/tableTypes';
import { appHistoryPush } from '@modules/app/appHistory';
import { ELogBusinessEventName, ELogBusinessEventType } from '@modules/logging/loggingConstants';
import { EOnboardingComponentsId } from '@modules/onboarding/types/onboardingTypes';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '@modules/role/selectors/guardSelector';
import { wizardStorageClearStepsAction } from '@modules/wizard/wizardActions';
import { EWizardName } from '@modules/wizard/wizardConstants';
import { EReportsConstructorSource } from '@pages/PageReportsConstructor/constants';
import { IReportsConstructorLocationData } from '@pages/PageReportsConstructor/types';
import { ExternalRoutePath, openExternalRoute } from '@routes/constants/externalRoutes';
import { CLIENT_ID, IRouteParams, RoutePath } from '@routes/constants/routesConstants';
import { stringFormat } from '@utils/stringFormat';

import {
    TTableButtonsGetTranslation,
    TTableButtonsLogBusinessEvent,
    TUseTableFooterButtonsOnboarding,
    TUseTableFooterButtonsUrl,
} from '../useTableFooterButtons';

const useTableFooterButtonMobileReport = (
    getTranslation: TTableButtonsGetTranslation,
    logBusinessEvent: TTableButtonsLogBusinessEvent,
    tableFooterName: TableFooterNameType,
) => {
    const dispatch = useDispatch();

    const history = useHistory<IReportsConstructorLocationData>();

    const { clientId } = useParams<IRouteParams>();

    const isReportsConstructorAvailable = useSelector(
        makeSelectGuardComponentEnabled(AccessComponentKeyEnum.reportsConstructor),
    );

    const isWizardAdditionalReportsEnabled =
        useSelector(makeSelectGuardComponentEnabled(AccessComponentKeyEnum.PageWizardAdditionalReports));

    return {
        className: 'table-footer__link link-transaction',
        dataName: 'report',
        icon: icons.transactionSmallIcon,
        id: AccessComponentKeyEnum.FooterButtonReports,
        ...mapTableFooterNameToOnboarding[tableFooterName],
        ...getTranslation(AccessComponentKeyEnum.FooterButtonReports),

        onClick: useCallback(() => {
            if (isReportsConstructorAvailable) {
                logBusinessEvent(ELogBusinessEventName.USER_ADD_REPORTS_CONSTRUCTOR, ELogBusinessEventType.common);

                if (
                    tableFooterName === TableFooterNameType.account ||
                    tableFooterName === TableFooterNameType.accountId ||
                    tableFooterName === TableFooterNameType.subscriberMobile
                ) {
                    const locationData = mapTableFooterNameToReportsConstructorSource(tableFooterName, clientId);
                    if (!!locationData) {
                        const path = mapRoutePathsToReportConstructorSource(locationData.source, clientId);
                        history.push(path, locationData);
                    }
                }
            } else {
                logBusinessEvent(ELogBusinessEventName.USER_ADD_REPORTS_MASTER_TO_STEP_1, ELogBusinessEventType.common);
                dispatch(wizardStorageClearStepsAction({ wizardName: EWizardName.ADDITIONAL_REPORTS }));
                const urlConfig = mapTableFooterNameToUrl[tableFooterName];
                if (!urlConfig) return;

                isWizardAdditionalReportsEnabled
                    ? appHistoryPush(urlConfig.url.replace(CLIENT_ID, clientId))
                    : openExternalRoute(stringFormat(urlConfig.external, clientId));
            }
        }, [
            clientId,
            dispatch,
            history,
            isReportsConstructorAvailable,
            isWizardAdditionalReportsEnabled,
            logBusinessEvent,
            tableFooterName]),
    };
};

const mapTableFooterNameToUrl: Partial<TUseTableFooterButtonsUrl> = {
    [TableFooterNameType.account]: {
        url: RoutePath.wizardAdditionalReportsFromAccounts,
        external: ExternalRoutePath.accountReport,
    },
    [TableFooterNameType.accountId]: {
        url: RoutePath.wizardAdditionalReportsFromAccountId,
        external: ExternalRoutePath.accountReportId,
    },
    [TableFooterNameType.subscriberId]: {
        url: RoutePath.wizardAdditionalReportsFromSubscriberId,
        external: ExternalRoutePath.mobileReportId,
    },
    [TableFooterNameType.subscriberMobile]: {
        url: RoutePath.wizardAdditionalReportsFromSubscribers,
        external: ExternalRoutePath.mobileReport,
    },
};

const mapTableFooterNameToOnboarding: TUseTableFooterButtonsOnboarding = {
    [TableFooterNameType.account]: {
        onboarding: EOnboardingComponentsId.accountsTableFooterButtonReports,
    },
    [TableFooterNameType.subscriberMobile]: {
        onboarding: EOnboardingComponentsId.subscriberTableFooterButtonReports,
    },
};

const mapRoutePathsToReportConstructorSource = (source?: keyof typeof EReportsConstructorSource, clientId?: string) => {
    switch (source) {
        case EReportsConstructorSource.ACCOUNT_LIST:
            return RoutePath.reportsConstructorFromAccountList;
        case EReportsConstructorSource.MOBILE_LIST:
            return RoutePath.reportsConstructorFromMobileList;
        case EReportsConstructorSource.ACCOUNT:
            if (clientId) return RoutePath.reportsConstructorFromAccount.replace(CLIENT_ID, clientId);
            else return RoutePath.reportsConstructor;
        default:
            return RoutePath.reportsConstructor;
    }
};

const mapTableFooterNameToReportsConstructorSource = (
    tableFooterName: TableFooterNameType,
    accountId?: string,
): IReportsConstructorLocationData | null => {
    switch (tableFooterName) {
        case TableFooterNameType.account:
            return {
                source: EReportsConstructorSource.ACCOUNT_LIST,
            };
        case TableFooterNameType.accountId:
            return {
                source: EReportsConstructorSource.ACCOUNT,
                bisAccountId: accountId ? Number(accountId) : undefined,
            };
        case TableFooterNameType.subscriberMobile:
            return {
                source: EReportsConstructorSource.MOBILE_LIST,
            };
        default:
            return null;
    }
};

export default useTableFooterButtonMobileReport;
